/**
 * Create a new form response on a family's behalf. Builds the form-service
 * answers payload (pre-ranking answers plus the staffer's school ranking) and
 * sends it in one createForm call (FORM_CREATE), so staff who lack FORM_EDIT on
 * the section can still start a response. Eligibility is checked separately via
 * the eligibility endpoint before this runs.
 */
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCreateFormForAdmin,
  useGetStaffCreatePrecheckKey,
} from "src/generated/hooks/form-service/queries";
import type { FormAnswers } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { FormAddressSchema } from "src/schemas/Address";
import { CustomQuestionAnswerSchema } from "src/schemas/CustomQuestionType";
import { Answer } from "src/services/formTemplate";
import {
  answerIsEmpty,
  getAddressAnswersInserts,
  getDateAnswersInserts,
  getFreeTextAnswersInserts,
  getGradesAnswersInserts,
  getMultiSelectAnswersInserts,
  getNumberAnswersInserts,
  getSingleSelectAnswersInserts,
} from "src/services/formTemplate/answer";
import { isNotNull } from "src/services/predicates";
import { Forms } from "src/services/url/OrgAdmin";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { useInvalidateFormList } from "./useFormsList";

const UuidSchema = z.string().uuid();

/**
 * Serialize custom-question answers into the form-service answers map. Mirrors the
 * admin edit pipeline (getAnswerInputForQuestionType + upsertFormAnswers): a
 * single-select field becomes a single-element option-id array, every other field
 * stays its raw string, and an empty field is null. Fully-empty custom questions
 * are omitted. The backend derives the guardian from the applicant and creates the
 * answer bank.
 */
function buildCustomQuestionAnswers(
  questions: readonly AF.Question<AF.WithId>[],
  answers: Answer.FormikValues
): FormAnswers {
  const result: FormAnswers = {};
  for (const question of questions) {
    if (question.type !== AF.CustomQuestionType) continue;
    const parsed = CustomQuestionAnswerSchema.safeParse(answers[question.id]);
    if (!parsed.success || answerIsEmpty(parsed.data.answersByQuestionId)) {
      continue;
    }

    const nestedQuestions = (question as AF.CustomQuestion<AF.WithId>)
      .nestedQuestions;
    const answersByQuestionId: Record<string, string | string[] | null> = {};
    for (const [fieldId, value] of Object.entries(
      parsed.data.answersByQuestionId
    )) {
      const fieldType = nestedQuestions.find((q) => q.id === fieldId)?.type;
      answersByQuestionId[fieldId] =
        fieldType === AF.SingleSelectType
          ? UuidSchema.safeParse(value).success
            ? [value]
            : []
          : value
          ? value
          : null;
    }

    result[question.id] = {
      answersByQuestionId,
      answerBankId: parsed.data.answerBankId,
      referenceId: parsed.data.referenceId,
    };
  }
  return result;
}

// The form does not exist when we build the answer map; form_id on the inserts is
// unused (only question ids and values are read), so a placeholder is fine.
const PLACEHOLDER_FORM_ID = "";

type SubmitArgs = {
  answers: Answer.FormikValues;
  questions: readonly AF.Question<AF.WithId>[];
  schoolId: uuid;
  schoolRankingSectionId: uuid;
};

interface UseCreateStaffFormResponseParams {
  formTemplateId: uuid;
  applicantId: uuid;
  onClose: () => void;
}

export function useCreateStaffFormResponse({
  formTemplateId,
  applicantId,
  onClose,
}: UseCreateStaffFormResponseParams) {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const invalidateFormList = useInvalidateFormList();

  const { mutateAsync: createForm } = useCreateFormForAdmin();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submit = React.useCallback(
    async ({
      answers,
      questions,
      schoolId,
      schoolRankingSectionId,
    }: SubmitArgs) => {
      setIsSubmitting(true);
      try {
        const freeTextAnswers = getFreeTextAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );
        const singleSelectAnswers = getSingleSelectAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );
        const multiSelectAnswers = getMultiSelectAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );
        const numberAnswers = getNumberAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );
        const dateAnswers = getDateAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );
        const standardAnswers = freeTextAnswers
          .concat(singleSelectAnswers)
          .concat(multiSelectAnswers)
          .concat(numberAnswers)
          .concat(dateAnswers);
        // getGradesAnswersInserts is typed as form_answer_insert_input but yields
        // grades rows carrying grade_config_id.
        const gradesAnswers = getGradesAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        ) as unknown as GQL.grades_answer_insert_input[];
        const addressAnswers = getAddressAnswersInserts(
          PLACEHOLDER_FORM_ID,
          questions,
          answers
        );

        const answersPayload: FormAnswers = {};
        standardAnswers.forEach((answer) => {
          if (!answer.question_id) return;
          if (answer.date_answer != null) {
            answersPayload[answer.question_id] = answer.date_answer;
          } else if (answer.number_answer != null) {
            answersPayload[answer.question_id] = answer.number_answer;
          } else if (answer.free_text_answer != null) {
            answersPayload[answer.question_id] = answer.free_text_answer;
          } else if (Array.isArray(answer.form_answer_options?.data)) {
            answersPayload[answer.question_id] = answer.form_answer_options.data
              .map((option) => option.form_question_option_id)
              .filter(isNotNull);
          }
        });
        gradesAnswers.forEach((answer) => {
          if (answer.question_id && answer.grade_config_id != null) {
            answersPayload[answer.question_id] = answer.grade_config_id;
          }
        });
        addressAnswers.forEach((answer) => {
          if (answer.question_id) {
            answersPayload[answer.question_id] =
              FormAddressSchema.parse(answer);
          }
        });

        Object.assign(
          answersPayload,
          buildCustomQuestionAnswers(questions, answers)
        );

        answersPayload[schoolRankingSectionId] = [
          { schoolId, rank: 0, isOverride: false, overrideReason: null },
        ];

        const createResult = await createForm({
          body: {
            data: {
              personId: applicantId,
              formTemplateId,
              answers: answersPayload,
            },
          },
        });
        const newFormId = createResult.data?.data.id;
        if (!newFormId) {
          throw new Error("Create form returned no id");
        }

        // The staff-create precheck is cached (global 5-minute staleTime), so
        // invalidate it or reopening for the same student would not detect the
        // form just created.
        await queryClient.invalidateQueries({
          queryKey: [useGetStaffCreatePrecheckKey],
        });
        // The forms list is a react-query query that will not refetch on its own;
        // invalidate it so the newly created response appears without a refresh.
        // The non-staff create path does the same in useCreateFormSubmit.
        invalidateFormList();

        onClose();
        toast({ title: "Form created", status: "success", isClosable: true });

        // This is a workaround to fix the issue where the modal is not closed
        // before the form is opened, which will disable scrolling on the form
        // detail page. This is the existing pattern used in useCreateFormSubmit.
        setTimeout(() => {
          navigate(
            organization
              .map(() =>
                Forms.view({
                  organization,
                  id: newFormId,
                  formTemplateId,
                  params: searchParams.toString(),
                })
              )
              .withDefault("#")
          );
        }, 100);
      } catch (error) {
        console.error(error);
        toast({
          title: "Could not create form",
          status: "error",
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      applicantId,
      createForm,
      formTemplateId,
      invalidateFormList,
      navigate,
      onClose,
      organization,
      queryClient,
      searchParams,
      toast,
    ]
  );

  return { submit, isSubmitting };
}
